import React, { useState } from 'react';
import { Text } from '@fiverr-private/typography';
import { Stack, Wrap } from '@fiverr-private/layout_components';
import { isLanguagesPair } from '@fiverr-private/dynamic_matching';
import { BriefSection } from '@fiverr-private/ai_brief';
import { EnrichedMetadataAnswerType, EnrichedMetadataQuestionType } from '../../../api';
import { AppI18n, appTranslate } from '../../../i18n';
import { EditComponentProps, ViewComponentProps } from '..';
import { NOT_SURE_METADATA_KEY } from '../../../constants';
import { EditActions } from '../EditActions';
import { ViewLanguagePairs } from './LanguagePairs/ViewLanguagePairs';
import { EditLanguagePairs } from './LanguagePairs/EditLanguagePairs';
import { ChoiceGroupEdit } from './ChoiceGroupEdit';
import { ComboboxEdit } from './ComboboxEdit';

const CHOICE_GROUP_THRESHOLD = 7;

export interface SelectProps {
  sectionContentState: MetadataSectionContentState;
  setSectionContentState: (value: MetadataSectionContentState['selectedAnswers']) => void;
  isMultiSelect: boolean;
  answerLimit: number;
  isMandatory?: boolean;
}

export interface LanguagesPairProps extends Omit<SelectProps, 'isMultiSelect' | 'answerLimit'> {
  section: BriefSection;
  onCancel: () => void;
  onEditEnd: () => void;
  shouldRemoveSection?: boolean;
}

export interface MetadataSectionContent {
  selectedAnswers: string[];
  allAnswers: EnrichedMetadataAnswerType[];
  question: EnrichedMetadataQuestionType;
}

export const MetadataViewComponent = ({ section }: ViewComponentProps) => {
  const metadata: MetadataSectionContent = (section as any).metadata;
  const { allAnswers } = metadata;
  const selectedAnswers = section.content as any as string[];

  const isLanguagesPairType = isLanguagesPair((section as any).metadata.question.metadata_type);

  if (isLanguagesPairType) {
    return <ViewLanguagePairs section={section} />;
  }

  if (selectedAnswers.includes(NOT_SURE_METADATA_KEY)) {
    return (
      <Text>
        <AppI18n k="sections.metadata.none_selected" />
      </Text>
    );
  }

  return (
    <Text>
      {selectedAnswers?.length > 0
        ? selectedAnswers.map((answer) => allAnswers.find((a) => a.slug === answer)?.display_name).join(', ')
        : section.mandatory && `(${appTranslate('sections.mandatory')})`}
    </Text>
  );
};

type MetadataSectionContentState = Omit<MetadataSectionContent, 'question'>;

export const MetadataEditComponent = ({
  section,
  onChange,
  onEditEnd,
  onCancel,
  shouldRemoveSection,
}: EditComponentProps) => {
  const [sectionContentState, setSectionContentState] = useState<MetadataSectionContentState>({
    ...(section as any).metadata,
    selectedAnswers: section.content,
  });
  const question: EnrichedMetadataQuestionType = (section as any).metadata.question;
  const isMultiSelect = question.selection_type === 'multi_select';

  const setSectionContentStateWrapper = (updatedValue: MetadataSectionContentState['selectedAnswers']) => {
    setSectionContentState((state) => ({ ...state, selectedAnswers: updatedValue }));
    onChange(updatedValue as any);
  };

  const editComponentProps = {
    sectionContentState,
    setSectionContentState: setSectionContentStateWrapper,
    isMultiSelect,
    answerLimit: question.answers_limit,
    isMandatory: section.mandatory,
  };

  const isSaveDisabled = !sectionContentState.selectedAnswers.length;

  if (isLanguagesPair(question.metadata_type)) {
    return (
      <EditLanguagePairs
        {...editComponentProps}
        onEditEnd={onEditEnd}
        onCancel={onCancel}
        shouldRemoveSection={shouldRemoveSection}
        section={section}
      />
    );
  }

  return (
    <Stack direction="column" gap="4">
      <Wrap gap="0">
        <Text size="b_sm">{question.question_title}</Text>
        {question.answers_limit > 1 && (
          <>
            &nbsp;
            <Text size="b_sm" color="bodySecondary">
              (<AppI18n k="sections.metadata.select_up_to" params={{ limit: question.answers_limit }} />)
            </Text>
          </>
        )}
      </Wrap>
      {question.answers.length > CHOICE_GROUP_THRESHOLD ? (
        <ComboboxEdit {...editComponentProps} />
      ) : (
        <ChoiceGroupEdit {...editComponentProps} />
      )}
      <EditActions
        onCancel={onCancel}
        onEditEnd={onEditEnd}
        isSaveDisabled={isSaveDisabled}
        shouldRemoveSection={shouldRemoveSection}
      />
    </Stack>
  );
};
